import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { PageResponse } from '../../types';
import { AdminPackageRow, PACKAGE_QUEUE_STATUSES, PACKAGE_SUSPENDABLE_STATUSES, PackageStatus } from '../../types/packages';
import { formatAed, formatInstant } from '../offers/offerReviewModel';
import PackageStatusChip from './PackageStatusChip';
import { formatPackageWindow, modeLabel, PACKAGE_STATUS_LABELS, priceText, unavailableReasonLabel } from './packageReviewModel';

export interface PackagesQueueTabProps {
  token: string;
  status: PackageStatus;
  onStatusChange: (status: PackageStatus) => void;
  /** Reports the PENDING_APPROVAL total whenever that filter loads, for the tab badge. */
  onPendingCount?: (count: number) => void;
}

const PAGE_SIZE = 20;

/** Spec 081b Flow D — the Packages tab of Service approvals. Keyed by status in the parent, so a new filter starts at page 0. */
export default function PackagesQueueTab(props: PackagesQueueTabProps) {
  const { token, status, onStatusChange, onPendingCount } = props;
  const [page, setPage] = useState(0);
  const [data, setData] = useState<PageResponse<AdminPackageRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.listPackageQueue(token, { status, page, size: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (status === 'PENDING_APPROVAL') onPendingCount?.(res.totalElements);
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load packages'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, status, page, onPendingCount]);

  const rows = data?.content ?? [];

  return (
    <div data-testid="packages-tab">
      <div className="admin-toolbar offer-queue__toolbar">
        <label className="admin-form-label" htmlFor="package-status-filter" style={{ margin: 0 }}>Status</label>
        <select id="package-status-filter" className="admin-select" value={status}
          onChange={(e) => onStatusChange(e.target.value as PackageStatus)}>
          {PACKAGE_QUEUE_STATUSES.map((s) => <option key={s} value={s}>{PACKAGE_STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      {error && <div className="admin-info-banner danger" role="alert" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : rows.length === 0 ? (
          <div className="admin-empty-state">
            <p>{status === 'PENDING_APPROVAL' ? 'No packages waiting' : `No ${PACKAGE_STATUS_LABELS[status].toLowerCase()} packages`}</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table offer-queue package-queue">
              <thead>
                <tr>
                  <th>Garage</th>
                  <th>Package</th>
                  <th>Services</th>
                  <th>Price</th>
                  <th>Window</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} data-testid="package-row" data-package-id={row.id}>
                    <td style={{ fontWeight: 500 }}>{row.garageName}</td>
                    <td>
                      <Link to={`/service-approvals/packages/${row.id}`} className="admin-link" style={{ fontWeight: 500 }}>{row.nameEn}</Link>
                      <div className="offer-queue__garage-meta">
                        <span className="offer-queue__label">{modeLabel(row)}</span>
                        <PackageStatusChip status={row.status} />
                        {row.unavailableReason && <span className="admin-badge warning">{unavailableReasonLabel(row.unavailableReason)}</span>}
                      </div>
                    </td>
                    <td>{row.itemCount}</td>
                    <td className="package-queue__price">
                      {row.pricingMode === 'FIXED' && row.originalTotal != null && <s>{formatAed(row.originalTotal)}</s>}{' '}
                      <strong>{priceText(row)}</strong>
                      {row.savingPercent != null && <div className="admin-text-muted">Saves {row.savingPercent}%</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatPackageWindow(row.startsAt, row.endsAt)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatInstant(row.submittedAt)}</td>
                    <td>
                      <div className="offer-queue__actions">
                        <Link to={`/service-approvals/packages/${row.id}`} className="admin-link">Review</Link>
                        {PACKAGE_SUSPENDABLE_STATUSES.includes(row.status) && (
                          <Link to={`/service-approvals/packages/${row.id}/edit`} className="admin-link" data-testid="package-row-edit">Edit</Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.totalPages > 1 && (
          <div className="admin-pagination">
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={data.number === 0 || loading}
              onClick={() => setPage(data.number - 1)}>Previous</button>
            <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)' }}>Page {data.number + 1} of {data.totalPages}</span>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={data.number + 1 >= data.totalPages || loading}
              onClick={() => setPage(data.number + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
